// The 401 handler for configureBackends' `onUnauthorized` — the fix from the
// dead-logout-button bug, promoted into the SDK so the next surface cannot
// re-introduce the race.
//
// Three rules, all load-bearing:
//   1. Single flight: a burst of parallel 401s produces ONE redirect, ever —
//      the latch never resets, because the redirect is a full page load.
//   2. The sign-out stand-down: during logout, teardown requests 401 as state
//      clears. Hijacking the in-flight hosted-logout navigation with a
//      /login redirect leaves the provider's session alive, and the next
//      on-load login signs the user straight back in silently.
//   3. The pending-login stand-down: a person who proved a login no account
//      owns yet holds a pending-account token and no session, and sits on the
//      account-choice screen. A call made without a session 401s there, and
//      reloading at /login would throw the screen away and start the sign-in
//      again, forever (found on an iPhone, 2026-10-19). The pending token is
//      what says this is a person mid-sign-in and not a dead session, so the
//      handler does nothing, and keeps its latch for a real dead session later.
import { brokeredLogin } from './brokeredLogin.js';
import { getPendingLoginToken } from './pendingLogin.js';
import { clearSession } from './sessionTokenStorage.js';

function holdsPendingLogin() {
    if (typeof sessionStorage === 'undefined') return false;
    return getPendingLoginToken() !== null;
}

/**
 * @param {Object} options
 * @param {String} [options.redirectTo] Where a dead session lands ('/login').
 * @param {Function} [options.navigate] Override for the page navigation —
 *   defaults to window.location.assign; a host with a router may inject its
 *   own. Never called when already at redirectTo, or while signing out.
 */
export function createUnauthorizedHandler({ redirectTo = '/login', navigate } = {}) {
    let handled = false;
    return function onUnauthorized() {
        if (handled) return;
        if (holdsPendingLogin()) return;
        handled = true;
        clearSession().finally(() => {
            if (brokeredLogin.isSigningOut()) return;
            if (typeof window === 'undefined') return;
            if (window.location.pathname === redirectTo) return;
            (navigate || window.location.assign.bind(window.location))(redirectTo);
        });
    };
}
